import {Box, Stack, Typography} from '@mui/material'
import {PerformanceCell, SimplePerformanceCell, GapCell, SparklineCell} from './PerformanceCell'
import {Metrics} from './useBenchmarkComparison'
import {formatNumber, numberPrecision} from '../../helpers/UtilityHelper'
import {RankCircle} from './PerformanceStyled'

// Left border style to separate column groups visually
const groupBorderStyle = () => ({borderLeft: '2px solid #e0e0e0'})

/**
 * Generates columns for Comparison component (simple labels)
 */
export function generateComparisonColumns(selectedMetrics) {
  return Metrics.filter((metric) => selectedMetrics.includes(metric.value)).map(
    (metric) => ({
      label: (
        <Stack spacing={0.25} alignItems='center'>
          <Typography variant='caption' fontWeight={700} sx={{fontSize: '0.68rem', lineHeight: 1.2, whiteSpace: 'nowrap'}}>
            {metric.label.replace(' %', '')}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.6rem'}}>%</Typography>
        </Stack>
      ),
      value: (row) => (
        <SimplePerformanceCell
          value={row[metric.value]?.value}
          count={row[metric.value]?.count}
          totalCount={undefined}
        />
      ),
      align: 'center',
    })
  )
}

/**
 * Compute weighted average row for forward benchmarking
 */
export function computeWeightedAverageRow(data) {
  if (!data || data.length === 0) return null

  const validData = data.filter((d) => d.courierId)
  if (validData.length === 0) return null

  const totalVolume = validData.reduce((s, d) => s + (d.totalPickedupCount || 0), 0)
  const totalDeliveryAttempted = validData.reduce(
    (s, d) => s + (d.totalDeliveryAttemptedCount || 0),
    0
  )
  const totalDeliveredShipments = validData.reduce(
    (s, d) => s + (d.totalDeliveredShipmentCount || 0),
    0
  )

  const weightedAvg = (field, weightField) => {
    const totalWeight = validData.reduce((s, d) => s + (d[weightField] || 0), 0)
    if (totalWeight === 0) return 0
    return (
      validData.reduce(
        (s, d) => s + (d[field] || 0) * (d[weightField] || 0),
        0
      ) / totalWeight
    )
  }

  const sumField = (field) =>
    validData.reduce((s, d) => s + (d[field] || 0), 0)

  const totalCodCount = sumField('codCount')
  const totalPrepaidCount = sumField('prepaidCount')
  const totalCodDelivered = sumField('codDeliveredCount')
  const totalPrepaidDelivered = sumField('prepaidDeliveredCount')

  const codDeliveryPct = totalCodCount > 0 ? (totalCodDelivered / totalCodCount) * 100 : 0
  const prepaidDeliveryPct =
    totalPrepaidCount > 0 ? (totalPrepaidDelivered / totalPrepaidCount) * 100 : 0

  return {
    _isWeightedAverage: true,
    name: 'Weighted Average',
    totalPickedupCount: totalVolume,
    overallDelivery: weightedAvg('overallDelivery', 'totalPickedupCount'),
    codDeliveryPercentage: codDeliveryPct,
    prepaidDeliveryPercentage: prepaidDeliveryPct,
    codPercentage: totalVolume > 0 ? (totalCodCount / totalVolume) * 100 : 0,
    firstDeliveryAttemptInTATPercentage: weightedAvg(
      'firstDeliveryAttemptInTATPercentage',
      'totalDeliveredShipmentCount'
    ),
    tatAdherence: weightedAvg('tatAdherence', 'totalDeliveredShipmentCount'),
    tatPlusOneAdherence: weightedAvg('tatPlusOneAdherence', 'totalDeliveredShipmentCount'),
    tatPlusTwoAdherence: weightedAvg('tatPlusTwoAdherence', 'totalDeliveredShipmentCount'),
    totalDeliveredCount: sumField('totalDeliveredCount'),
    codCount: totalCodCount,
    prepaidCount: totalPrepaidCount,
    codDeliveredCount: totalCodDelivered,
    prepaidDeliveredCount: totalPrepaidDelivered,
    totalDeliveredShipmentCount: totalDeliveredShipments,
    deliveredInTATCount: sumField('deliveredInTATCount'),
    deliveredInTATPlusOneCount: sumField('deliveredInTATPlusOneCount'),
    deliveredInTATPlusTwoCount: sumField('deliveredInTATPlusTwoCount'),
    firstDeliveryAttemptInTATCount: sumField('firstDeliveryAttemptInTATCount'),
    totalDeliveryAttemptedCount: totalDeliveryAttempted,
  }
}

/**
 * Generates columns for OverallForward component with visibility toggling
 */
export function generateOverallForwardColumns(
  sortedData,
  visibility = {showDelivery: true, showTAT: true, showVolume: true},
  trendData,
  monthNames,
  monthlyData
) {
  const columns = [
    // # (rank)
    {
      value: (row) => {
        if (row._isWeightedAverage) {
          return (
            <Typography variant='body2' fontWeight={700} color='text.secondary'>
              -
            </Typography>
          )
        }
        const rank =
          sortedData
            .filter((i) => !i._isWeightedAverage)
            .findIndex((item) => item.name === row.name) + 1
        return <RankCircle rank={rank}>{rank}</RankCircle>
      },
      label: '#',
      align: 'center',
    },
    // Courier Partner
    {
      value: (row) => {
        if (row._isWeightedAverage) {
          return (
            <Box>
              <Typography variant='body2' fontWeight={700} color='text.primary'>
                Weighted Average
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {formatNumber(row.totalPickedupCount || 0)} total shipments
              </Typography>
            </Box>
          )
        }
        return (
          <Box>
            <Typography variant='body2' fontWeight={600} sx={{color: 'text.primary'}}>
              {row.name}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              {formatNumber(row.totalPickedupCount || 0)} shipments
            </Typography>
          </Box>
        )
      },
      label: 'Service Type',
      align: 'left',
      group:'delivery'
    },
  ]

  // Total Shipments column (first in its group — add left border)
  if (visibility.showVolume) {
    columns.push({
      value: (row) => (
        <Typography variant='body2' fontWeight={600}>
          {formatNumber(row.totalPickedupCount || 0)}
        </Typography>
      ),
      label: 'Total Shipments',
      align: 'center',
      group: 'volume',
      style: groupBorderStyle,
    })
  }

  // DELIVERY PERFORMANCE group
  if (visibility.showDelivery) {
    columns.push(
      // Overall (first in group - add left border)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.overallDelivery}
            count={row.totalDeliveredCount}
            totalCount={row.totalPickedupCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              Overall
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Delivery %
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'delivery',
        style: groupBorderStyle,
      },
      // COD Delivery
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.codDeliveryPercentage}
            columnName='codDeliveryPercentage'
            count={row.codDeliveredCount}
            totalCount={row.codCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              COD
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Delivery %
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'delivery',
      },
      // Prepaid Delivery
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.prepaidDeliveryPercentage}
            columnName='prepaidDeliveryPercentage'
            count={row.prepaidDeliveredCount}
            totalCount={row.prepaidCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              Prepaid
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Delivery %
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'delivery',
      },
      // COD% Shipments
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.codPercentage}
            columnName='codDeliveryPercentage'
            count={row.codCount}
            totalCount={row.totalPickedupCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              COD%
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Shipments
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'delivery',
      }
    )
  }

  // TAT PERFORMANCE group
  if (visibility.showTAT) {
    columns.push(
      // First Attempt (first in group - add left border)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.firstDeliveryAttemptInTATPercentage}
            count={row.firstDeliveryAttemptInTATCount}
            totalCount={row.totalDeliveredShipmentCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              1st Attempt
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              In TAT %
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'tat',
        style: groupBorderStyle,
      },
      // In TAT (Delivery)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.tatAdherence}
            columnName='t'
            count={row.deliveredInTATCount}
            totalCount={row.totalDeliveredShipmentCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              In TAT
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              (Delivery)
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'tat',
      },
      // TAT+1 (Delivery)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.tatPlusOneAdherence}
            columnName='tplusone'
            count={
              (row.deliveredInTATPlusOneCount || 0) + (row.deliveredInTATCount || 0)
            }
            totalCount={row.totalDeliveredShipmentCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              TAT+1
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              (Delivery)
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'tat',
      },
      // TAT+2 (Delivery)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={row.tatPlusTwoAdherence}
            columnName='tplustwo'
            count={
              (row.deliveredInTATPlusTwoCount || 0) +
              (row.deliveredInTATPlusOneCount || 0) +
              (row.deliveredInTATCount || 0)
            }
            totalCount={row.totalDeliveredShipmentCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              TAT+2
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              (Delivery)
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'tat',
      },
      // TAT>2 (Delivery)
      {
        value: (row) => (
          <SimplePerformanceCell
            value={100}
            columnName='tatplusthree'
            count={row.totalDeliveredShipmentCount}
            totalCount={row.totalDeliveredShipmentCount}
          />
        ),
        label: (
          <Stack spacing={0.3} alignItems='center'>
            <Typography variant='body2' fontWeight={600}>
              {'TAT>2'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              (Delivery)
            </Typography>
          </Stack>
        ),
        align: 'center',
        group: 'tat',
      }
    )
  }

  // 30d TREND column (sparkline)
  if (trendData && trendData.size > 0) {
    columns.push({
      value: (row) => {
        if (row._isWeightedAverage) {
          return (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          )
        }
        const trend = trendData.get(row.courierId)
        return <SparklineCell data={trend || []} />
      },
      label: (
        <Stack spacing={0.3} alignItems='center'>
          <Typography variant='body2' fontWeight={600}>
            Trend
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Delivery %
          </Typography>
        </Stack>
      ),
      align: 'center',
      group: 'trend',
      style: groupBorderStyle,
    })
  }

  // MONTHLY SPLIT columns — when date range spans multiple months
  if (monthNames && monthNames.length > 1 && monthlyData && monthlyData.size > 0) {
    for (const month of monthNames) {
      // Volume for this month
      if (visibility.showVolume) {
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <Typography variant='body2' fontWeight={600}>
                {formatNumber(mRow?.totalPickedupCount || 0)}
              </Typography>
            )
          },
          label: 'Shipments',
          align: 'center',
          style: groupBorderStyle,
        })
      }

      // Overall Delivery % for this month
      if (visibility.showDelivery) {
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <SimplePerformanceCell
                value={mRow?.overallDelivery}
                count={mRow?.totalDeliveredCount}
                totalCount={mRow?.totalPickedupCount}
              />
            )
          },
          label: (
            <Stack spacing={0.3} alignItems='center'>
              <Typography variant='body2' fontWeight={600}>Overall</Typography>
              <Typography variant='caption' color='text.secondary'>Delivery %</Typography>
            </Stack>
          ),
          align: 'center',
          ...(!visibility.showVolume ? {style: groupBorderStyle} : {}),
        })

        // COD Delivery % for this month
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <SimplePerformanceCell
                value={mRow?.codDeliveryPercentage}
                columnName='codDeliveryPercentage'
                count={mRow?.codDeliveredCount}
                totalCount={mRow?.codCount}
              />
            )
          },
          label: (
            <Stack spacing={0.3} alignItems='center'>
              <Typography variant='body2' fontWeight={600}>COD</Typography>
              <Typography variant='caption' color='text.secondary'>Delivery %</Typography>
            </Stack>
          ),
          align: 'center',
        })

        // Prepaid Delivery % for this month
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <SimplePerformanceCell
                value={mRow?.prepaidDeliveryPercentage}
                columnName='prepaidDeliveryPercentage'
                count={mRow?.prepaidDeliveredCount}
                totalCount={mRow?.prepaidCount}
              />
            )
          },
          label: (
            <Stack spacing={0.3} alignItems='center'>
              <Typography variant='body2' fontWeight={600}>Prepaid</Typography>
              <Typography variant='caption' color='text.secondary'>Delivery %</Typography>
            </Stack>
          ),
          align: 'center',
        })
      }

      // In TAT (Delivery) for this month
      if (visibility.showTAT) {
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <SimplePerformanceCell
                value={mRow?.tatAdherence}
                columnName='t'
                count={mRow?.deliveredInTATCount}
                totalCount={mRow?.totalDeliveredShipmentCount}
              />
            )
          },
          label: (
            <Stack spacing={0.3} alignItems='center'>
              <Typography variant='body2' fontWeight={600}>In TAT</Typography>
              <Typography variant='caption' color='text.secondary'>(Delivery)</Typography>
            </Stack>
          ),
          align: 'center',
          ...(!visibility.showDelivery && !visibility.showVolume ? {style: groupBorderStyle} : {}),
        })

        // 1st Attempt In TAT for this month
        columns.push({
          value: (row) => {
            const mRow = monthlyData.get(row.courierId)?.[month]
            return (
              <SimplePerformanceCell
                value={mRow?.firstDeliveryAttemptInTATPercentage}
                count={mRow?.firstDeliveryAttemptInTATCount}
                totalCount={mRow?.totalDeliveredShipmentCount}
              />
            )
          },
          label: (
            <Stack spacing={0.3} alignItems='center'>
              <Typography variant='body2' fontWeight={600}>1st Attempt</Typography>
              <Typography variant='caption' color='text.secondary'>In TAT %</Typography>
            </Stack>
          ),
          align: 'center',
        })
      }
    }
  }

  return columns
}

/**
 * Generate column groups for the table header
 */
export function generateForwardColumnGroups(
  visibility,
  hasTrend = false,
  monthNames
) {
  const groups = []

  // # + Courier Partner are always visible
  groups.push({label: '', colSpan: 2})

  if (visibility.showVolume) {
    groups.push({label: '', colSpan: 1})
  }

  if (visibility.showDelivery) {
    groups.push({label: 'DELIVERY PERFORMANCE', colSpan: 4})
  }

  if (visibility.showTAT) {
    groups.push({label: 'TAT PERFORMANCE', colSpan: 5})
  }

  if (hasTrend) {
    groups.push({label: '', colSpan: 1})
  }

  // Monthly split groups
  if (monthNames && monthNames.length > 1) {
    for (const month of monthNames) {
      let colSpan = 0
      if (visibility.showVolume) colSpan += 1
      if (visibility.showDelivery) colSpan += 3
      if (visibility.showTAT) colSpan += 2
      if (colSpan > 0) {
        groups.push({label: month.toUpperCase(), colSpan})
      }
    }
  }

  return groups
}
